'use client'

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react'
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from '@headlessui/react'
import { ChatBubbleLeftRightIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { usePathname } from 'next/navigation'
import { useQuery } from '@tanstack/react-query'
import { useStore } from '@/store'
import { useTurnstile } from '@/components/Turnstile'
import { ChatSendController } from '@/lib/support-chat/ChatSendController'
import {
  chatRecovery,
  clearChatRecovery,
} from '@/lib/support-chat/recovery'
import {
  chatAccessLost,
  chatError,
  chatRequest,
  ChatRequestError,
  withChatLock,
} from '@/lib/support-chat/client'
import type { ChatMessageInput, ChatRead } from '@/lib/support-chat/types'
import { bingIfPrimed, primeChatBing, staffRepliesToHear } from '@/lib/support-chat/chatBing'
import { ChatComposer } from './ChatComposer'
import { ChatEmailPanel } from './ChatEmailPanel'
import { ConversationHistory } from './ConversationHistory'

const messengerPath = '/support-chat'

export default function SupportMessenger() {
  const pathname = usePathname() ?? '/'
  const customer = useStore(state => state.user)
  const hydrated = useStore(state => state.hydrated)
  const authResolved = useStore(state => state.authResolved)
  if (
    !hydrated ||
    !authResolved ||
    !(pathname === messengerPath || pathname.startsWith(`${messengerPath}/`))
  )
    return null
  return (
    <CustomerMessenger
      key={customer?.id ?? 'guest'}
      customerId={customer?.id}
      accountEmail={customer?.email}
    />
  )
}

function CustomerMessenger({
  customerId,
  accountEmail,
}: {
  customerId?: number
  accountEmail?: string
}) {
  const scope = customerId ? `customer:${customerId}` : 'guest'
  const [open, setOpen] = useState(false)
  const [confirmEnd, setConfirmEnd] = useState(false)
  const [ending, setEnding] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [restored, setRestored] = useState<ChatMessageInput | undefined>()
  const [composerKey, setComposerKey] = useState('new')
  const [seen, setSeen] = useState<string[]>([])
  const typingAt = useRef(0)
  const heard = useRef<string[] | null>(null)
  const { executeTurnstile } = useTurnstile('support-chat')
  const requireCurrentAccount = useCallback(() => {
    const account = useStore.getState()
    if (!account.authResolved || account.user?.id !== customerId) {
      throw new Error(
        'Your account changed. Open chat again before continuing.'
      )
    }
  }, [customerId])
  const [controller] = useState(
    () =>
      new ChatSendController({
        scope,
        recovery: chatRecovery,
        lock: withChatLock,
        submit: async (attempt, captcha) => {
          requireCurrentAccount()
          const result = await chatRequest<{ ticketId: number }>({
            method: 'POST',
            browserContext: attempt.browserContext,
            body: {
              action: attempt.action,
              input: attempt.input,
              turnstileToken: captcha,
            },
          })
          return result.data.ticketId
        },
      })
  )
  const sendState = useSyncExternalStore(
    controller.subscribe,
    controller.snapshot,
    controller.snapshot
  )
  const query = useQuery({
    queryKey: ['website-chat', scope],
    queryFn: ({ signal }) =>
      withChatLock(async () => {
        requireCurrentAccount()
        const response = await chatRequest<ChatRead>({ signal })
        requireCurrentAccount()
        return response
      }),
    enabled: true,
    refetchInterval: open ? 5_000 : 15_000,
    staleTime: 0,
    retry: false,
    gcTime: 0,
  })
  const accessLost = chatAccessLost(query.error)
  const conversation = accessLost ? null : query.data?.data.conversation
  const browserContext =
    query.error instanceof ChatRequestError && query.error.browserContext
      ? query.error.browserContext
      : query.data?.browserContext
  const availability = query.data?.data.availability
  const pending = sendState.pending
  const teamOffline =
    Boolean(availability?.enabled) &&
    !availability?.teamAvailable &&
    !conversation &&
    !pending
  const unread = !open
    ? (conversation?.messages.filter(
        message => message.authorName !== 'You' && !seen.includes(message.id)
      ).length ?? 0)
    : 0

  useEffect(() => {
    void (async () => {
      await controller.restore()
      const saved = controller.snapshot().pending
      if (!saved) return
      setRestored(saved.input)
      setComposerKey(saved.input.requestId)
    })()
  }, [controller])
  if (open && conversation) {
    const ids = conversation.messages.map(message => message.id)
    if (
      seen.length !== ids.length ||
      seen.some((id, index) => id !== ids[index])
    ) {
      setSeen(ids)
    }
  }
  const conversationId = conversation?.ticketId
  useEffect(() => {
    const messages = conversation?.messages
    if (!messages) return
    const replies = staffRepliesToHear(heard.current, messages)
    heard.current = messages.map(message => message.id)
    if (replies.length > 0) bingIfPrimed()
  }, [conversation])
  useEffect(() => {
    if (!open || !conversationId || !browserContext || accessLost) return
    let active = true
    const heartbeat = () =>
      withChatLock(async () => {
        if (!active || document.visibilityState !== 'visible') return
        requireCurrentAccount()
        await chatRequest({
          method: 'POST',
          browserContext,
          body: {
            action: 'heartbeat',
            input: { typing: Date.now() - typingAt.current < 10_000 },
          },
        })
      }).catch(() => {})
    void heartbeat()
    const timer = window.setInterval(() => {
      void heartbeat()
    }, 10_000)
    return () => {
      active = false
      window.clearInterval(timer)
    }
  }, [open, conversationId, browserContext, accessLost, requireCurrentAccount])

  async function refresh() {
    requireCurrentAccount()
    await query.refetch()
    await controller.restore()
  }
  async function send(input: ChatMessageInput) {
    primeChatBing()
    if (!browserContext) return false
    setActionError(null)
    setNotice(null)
    try {
      const captcha = conversation ? undefined : await executeTurnstile()
      requireCurrentAccount()
      if (controller.snapshot().pending) await controller.forget()
      const id = await controller.send(
        conversation ? 'reply' : 'start',
        input,
        browserContext,
        captcha
      )
      if (!id) return false
      typingAt.current = 0
      await refresh()
      return true
    } catch (error) {
      setActionError(chatError(error))
      return false
    }
  }
  async function end() {
    if (!browserContext) return
    setEnding(true)
    setActionError(null)
    try {
      await withChatLock(async () => {
        requireCurrentAccount()
        await chatRequest({ method: 'DELETE', browserContext })
        await clearChatRecovery(browserContext)
      })
      setConfirmEnd(false)
      setRestored(undefined)
      setComposerKey(crypto.randomUUID())
      setSeen([])
      setNotice('This chat has ended.')
      await refresh()
    } catch (error) {
      setActionError(chatError(error))
    } finally {
      setEnding(false)
    }
  }

  if (teamOffline || (query.isPending && !conversation && !pending)) return null

  return (
    <>
      <button
        type="button"
        onClick={() => {
          primeChatBing()
          setOpen(true)
          void controller.restore()
        }}
        className="group fixed right-4 bottom-4 z-40 flex min-h-12 items-center gap-2 rounded-full bg-vi-red px-5 py-3 text-sm font-semibold text-white shadow-lg motion-safe:transition motion-safe:duration-200 motion-safe:ease-out hover:-translate-y-0.5 hover:bg-red-800 hover:shadow-xl active:translate-y-0 active:scale-[0.98] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white sm:right-6 sm:bottom-6"
        aria-label={
          unread
            ? `Chat with us, ${unread} new ${unread === 1 ? 'reply' : 'replies'}`
            : 'Chat with us'
        }
      >
        <ChatBubbleLeftRightIcon
          className="size-5 motion-safe:transition motion-safe:duration-200 group-hover:-translate-y-px group-hover:translate-x-px"
          aria-hidden="true"
        />
        Chat
        with us
        {unread > 0 && (
          <span className="rounded-full bg-white px-2 py-0.5 text-xs text-vi-red">
            {unread}
          </span>
        )}
      </button>
      <Dialog
        open={open}
        onClose={setOpen}
        unmount={false}
        className="relative z-[80]"
      >
        <DialogBackdrop className="fixed inset-0 bg-black/20" />
        <div className="fixed inset-0 flex items-end justify-end p-0 sm:p-6">
          <DialogPanel
            className="flex h-[min(720px,92dvh)] w-full flex-col overflow-hidden rounded-t-2xl bg-white shadow-2xl sm:w-100 sm:rounded-2xl"
            onPointerDown={() => primeChatBing()}
          >
            <header className="flex shrink-0 items-center justify-between gap-3 bg-vi-red px-4 py-3 text-white">
              <div className="flex items-center gap-2.5">
                <span
                  className="flex size-8 shrink-0 items-center justify-center rounded-full bg-white text-[11px] font-bold text-vi-red"
                  aria-hidden="true"
                >
                  VI
                </span>
                <div className="min-w-0">
                  <DialogTitle className="text-base font-semibold leading-snug">
                    Vanishing Inc. Support Wizards
                  </DialogTitle>
                  <p className="text-xs text-white/70">
                    {query.isError
                      ? 'Connection unavailable'
                      : availability?.teamAvailable
                        ? 'Our team is available'
                        : 'Support chat'}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close chat"
                className="rounded-full p-2 text-white/80 hover:bg-white/10 hover:text-white"
              >
                <XMarkIcon className="size-5" />
              </button>
            </header>
            <div className="flex min-h-0 flex-1 flex-col px-4 py-3">
              {query.isPending && (
                <p role="status" className="text-sm text-gray-500">
                  Opening your conversation…
                </p>
              )}
              {availability && !availability.enabled && !query.isError && (
                <p role="status" className="text-sm text-gray-600">
                  Website chat is unavailable at the moment.
                </p>
              )}
              {query.isError && (
                <div className="space-y-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-900">
                  <p role="alert">{chatError(query.error)}</p>
                  <button
                    className="font-semibold underline"
                    onClick={() => void refresh()}
                  >
                    Try again
                  </button>
                </div>
              )}
              {!conversation && !query.isPending && !query.isError && (
                <div className="flex flex-1 flex-col items-center justify-center px-6 text-center">
                  <span
                    className="flex size-14 items-center justify-center rounded-full bg-vi-red text-base font-semibold text-white"
                    aria-hidden="true"
                  >
                    VI
                  </span>
                  <p className="mt-4 text-base font-semibold text-gray-950">
                    The Support Wizards
                  </p>
                  <p className="mt-1 text-sm leading-5 text-gray-500">
                    Send a message and we’ll pick it up here.
                  </p>
                </div>
              )}
              {conversation && browserContext && (
                <ConversationHistory
                  key={`${browserContext}:${conversation.ticketId}`}
                  conversation={conversation}
                  browserContext={browserContext}
                />
              )}
              {conversation &&
                browserContext &&
                availability?.emailHandoffAvailable &&
                !query.isError && (
                  <div className="mt-3 shrink-0">
                    <ChatEmailPanel
                      key={browserContext}
                      browserContext={browserContext}
                      verification={conversation.emailVerification}
                      accountEmail={accountEmail}
                      onChange={refresh}
                    />
                  </div>
                )}
              {accessLost && !notice && (
                <p role="status" className="text-sm text-gray-700">
                  This chat has ended. If you still need help, email us and we
                  will pick it up there.
                </p>
              )}
            </div>
            <div className="shrink-0 space-y-2 px-3 pt-1 pb-3">
              {notice && (
                <p role="status" className="px-1 text-sm text-gray-600">
                  {notice}
                </p>
              )}
              {(actionError || sendState.error) && (
                <p role="alert" className="px-1 text-sm text-red-700">
                  {actionError ?? sendState.error}
                </p>
              )}
              {!sendState.ready && !query.isPending && (
                <button
                  type="button"
                  className="px-1 text-sm underline"
                  onClick={() => void controller.restore()}
                >
                  Retry message recovery
                </button>
              )}
              {browserContext && (conversation || accessLost) && (
                <div className="flex justify-end px-1">
                  {confirmEnd ? (
                    <div className="flex flex-wrap items-center justify-end gap-2">
                      <p className="text-xs text-gray-600">End this chat?</p>
                      <button
                        type="button"
                        className="text-xs font-semibold text-vi-red underline disabled:opacity-60"
                        disabled={ending}
                        onClick={() => void end()}
                      >
                        {ending ? 'Ending…' : 'End chat now'}
                      </button>
                      <button
                        type="button"
                        className="text-xs font-semibold text-gray-500 hover:text-gray-950"
                        onClick={() => setConfirmEnd(false)}
                      >
                        Keep chatting
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="text-xs font-medium text-gray-400 hover:text-gray-700"
                      onClick={() => setConfirmEnd(true)}
                    >
                      End chat
                    </button>
                  )}
                </div>
              )}
              {!accessLost && browserContext && availability?.enabled && (
                <ChatComposer
                  key={`${conversation?.ticketId ?? 'start'}:${composerKey}`}
                  initial={restored}
                  starting={!conversation}
                  signedIn={Boolean(customerId)}
                  disabled={
                    !sendState.ready ||
                    sendState.busy ||
                    ending ||
                    query.isError
                  }
                  onTyping={() => {
                    typingAt.current = Date.now()
                    setNotice(null)
                  }}
                  onSend={send}
                />
              )}
            </div>
          </DialogPanel>
        </div>
      </Dialog>
    </>
  )
}
